import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Rocket, AlertCircle, HelpCircle } from "lucide-react";
import MoonbucksLogo from "../components/MoonbucksLogo";
import { Button } from "../components/ui/button";
import AlertClassBadge from "../components/AlertClassBadge";
import ScorePill from "../components/ScorePill";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  WIN_TP:    { bg: "bg-emerald-100 text-emerald-800", label: "WIN · TP" },
  WIN_MOON:  { bg: "bg-amber-100 text-amber-900",     label: "🌙 MOON" },
  WIN_TIME:  { bg: "bg-emerald-50 text-emerald-700",  label: "WIN · time" },
  LOSS_SL:   { bg: "bg-red-100 text-red-800",         label: "STOP" },
  LOSS_TIME: { bg: "bg-red-50 text-red-700",          label: "LOSS · time" },
  ACTIVE:    { bg: "bg-blue-50 text-blue-700",        label: "LIVE" },
  UNKNOWN:   { bg: "bg-neutral-100 text-neutral-600", label: "—" },
};

export default function TrackRecord() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const apiDays = days === 9999 ? 3650 : days; // 10 years = effectively "since inception"
    // Clear stale data on tab-switch so the previous window's headline
    // doesn't linger under the newly-highlighted tab for 1-2s. UX polish.
    setData(null);
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/track-record?days=${apiDays}&limit=200`).then((r) => {
      if (!alive) return;
      setData({ ...r.data, window_days: days });
      setLoading(false);
    }).catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="bg-mb-sand min-h-screen">
      {/* Lightweight public header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="tr-logo">
            <div className="w-9 h-9 rounded-full bg-mb-emerald flex items-center justify-center text-white">
              <MoonbucksLogo className="w-5 h-5" />
            </div>
            <span className="font-serif-hero text-2xl leading-none">Moonbucks</span>
            <span className="hidden sm:inline-block text-xs text-neutral-400 mt-1">/ alerts</span>
          </Link>
          <Link to="/register">
            <Button className="rounded-md bg-mb-emerald hover:bg-mb-emerald-dark text-white" data-testid="tr-cta-signup">
              Get Moonbucks
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <Link to="/" className="inline-flex items-center text-sm text-neutral-600 hover:text-mb-emerald mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <div className="label-uppercase text-mb-emerald">Live Track Record</div>
        <h1 className="font-display font-semibold text-5xl lg:text-6xl tracking-tight mt-2 max-w-3xl leading-[0.95]">
          Every alert. Every outcome. <span className="text-mb-emerald">Public.</span>
        </h1>
        <p className="mt-4 text-lg text-neutral-600 max-w-2xl">
          We replay every published alert against actual market price action since it went out — TP1 hits, moon shots, stop-outs, and live trades.
          No cherry-picking.
        </p>

        {/* Explicit framing: live = post-desk-vetting */}
        <div className="mt-4 p-4 rounded-md mb-card-elevated flex items-start gap-3 max-w-3xl" data-testid="tr-framing">
          <div className="label-uppercase text-[10px]" style={{ color: "var(--mb-emerald)", letterSpacing: "0.16em" }}>NOTE</div>
          <div className="text-xs text-neutral-600 leading-relaxed">
            These are <span className="font-semibold text-mb-ink">live published alerts</span> — algorithm candidates that <span className="font-semibold text-mb-ink">also passed the desk&apos;s vetting</span> (sentiment, conviction, timing).
            For the pure-algorithm performance before any human filter, see the{" "}
            <Link to="/app/backtest" className="text-mb-emerald font-semibold underline-offset-2 hover:underline">12-month backtest →</Link>.
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="window-tabs">
          {[14, 30, 60, 90, 9999].map((d) => (
            <Button
              key={d}
              variant={days === d ? "default" : "outline"}
              className={`rounded-md ${days === d ? "bg-mb-emerald hover:bg-mb-emerald-dark text-white" : ""}`}
              onClick={() => setDays(d)}
              data-testid={`window-${d}`}
            >
              {d === 9999 ? "Since inception" : `Last ${d} days`}
            </Button>
          ))}
        </div>

        {data?.inception_date && (
          <p className="mt-3 text-xs text-neutral-500 font-mono-data" data-testid="tr-inception">
            Tracking since {new Date(data.inception_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
            {" · "}
            {data.total_alerts} published alerts {data.window_days < 9999 ? `in the last ${data.window_days} days` : "to date"}
          </p>
        )}

        {!data && <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-mb-emerald" /></div>}

        {data && (
          <>
            {/* Working Now strip — public-safe live proof. No symbols leaked;
                aggregates computed server-side from the ACTIVE outcomes. */}
            {data.working_now && data.working_now.total > 0 && (
              <div
                className="mt-6 rounded-lg border p-5 flex flex-wrap items-center gap-6 bg-gradient-to-br from-emerald-50 to-white"
                style={{ borderColor: "rgba(16, 185, 129, 0.35)" }}
                data-testid="tr-working-now-strip"
              >
                <div>
                  <div className="label-uppercase text-[10px] text-emerald-700 tracking-widest">
                    Live right now
                  </div>
                  <div className="font-mono-data text-3xl font-extrabold mt-1 text-emerald-700" data-testid="tr-working-in-profit">
                    {data.working_now.in_profit}
                    <span className="opacity-50 text-xl">/{data.working_now.total}</span>
                    <span className="text-xs uppercase tracking-wider ml-2 text-neutral-500">in profit</span>
                  </div>
                </div>
                <div>
                  <div className="label-uppercase text-[10px] text-neutral-500 tracking-widest">
                    Avg across live book
                  </div>
                  <div
                    className={`font-mono-data text-3xl font-extrabold mt-1 ${data.working_now.avg_return_pct >= 0 ? "text-emerald-700" : "text-red-600"}`}
                    data-testid="tr-working-avg"
                  >
                    {data.working_now.avg_return_pct >= 0 ? "+" : ""}{data.working_now.avg_return_pct}%
                  </div>
                </div>
                {data.working_now.best_return_pct !== 0 && (
                  <div>
                    <div className="label-uppercase text-[10px] text-neutral-500 tracking-widest">
                      Best live trade
                    </div>
                    <div className="font-mono-data text-3xl font-extrabold mt-1 text-emerald-700" data-testid="tr-working-best">
                      +{data.working_now.best_return_pct}%
                    </div>
                  </div>
                )}
                <div className="ml-auto max-w-xs">
                  <p className="text-xs text-neutral-500 leading-relaxed">
                    These are published alerts <strong>open right this moment</strong> — tickers stay behind the paywall. Subscribers see the names.
                  </p>
                </div>
              </div>
            )}

            {/* Per-class breakdown — leading because it tells the dual-strategy story */}
            {data.by_class && (data.by_class.steady?.total > 0 || data.by_class.moonshot?.total > 0) && (
              <div className="mt-6 grid md:grid-cols-2 gap-3" data-testid="tr-by-class">
                <ClassBucket kind="steady" title="☕ Steady tier" stats={data.by_class.steady} />
                <ClassBucket kind="moonshot" title="🚀 Moonshot tier" stats={data.by_class.moonshot} />
              </div>
            )}

            {/* Blended stats — secondary, smaller */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="tr-headline">
              <Big label="Blended WR" val={`${data.win_rate}%`} />
              <Big label="Avg Return" val={`${data.avg_return_pct >= 0 ? "+" : ""}${data.avg_return_pct}%`} />
              <Big label="🌙 Moon Hits" val={data.moon_hits} />
              <Big label="Closed" val={`${data.wins} W · ${data.losses} L`} />
              <Big label="Live" val={data.active} />
            </div>
            {/* Exclusion disclosure — honest footnote when any alerts had
                incomplete data and couldn't be scored. Keeps "every trade"
                claim defensible without hiding the exclusion. */}
            {data.excluded && data.excluded.count > 0 && (
              <div
                className="mt-2 text-[11px] text-neutral-500 flex items-start gap-1.5"
                data-testid="tr-exclusion-footnote"
              >
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
                <span>
                  Win-rate denominator excludes <strong>{data.excluded.count}</strong> alert{data.excluded.count === 1 ? "" : "s"} with incomplete data
                  {data.excluded.symbols && data.excluded.symbols.length > 0 && (
                    <> ({data.excluded.symbols.join(", ")})</>
                  )}
                  . These never produced a resolvable P&amp;L.
                </span>
              </div>
            )}

            {/* Loss curve disclosure — the Schwab/Fidelity move */}
            {((data.loss_curve && data.losses > 0) || (data.hold_stats && data.hold_stats.avg_days_held > 0)) && (
              <div className="mt-6 bg-white border border-mb-border rounded-lg p-5" data-testid="tr-loss-curve">
                {data.losses > 0 && (
                  <>
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div className="label-uppercase text-neutral-500">Risk profile</div>
                      <div className="text-xs text-neutral-500">How bad can it get? Look at the worst case, not just the average.</div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-neutral-500">Worst single trade</div>
                        <div className={`font-mono-data font-bold text-lg mt-0.5 ${data.loss_curve.max_loss_pct <= -7 ? "text-red-700" : "text-neutral-700"}`}>
                          {data.loss_curve.max_loss_pct}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-500">Avg of losing trades</div>
                        <div className="font-mono-data font-bold text-lg mt-0.5 text-red-700">{data.loss_curve.avg_loss_pct}%</div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-500">Stops held</div>
                        <div className="font-mono-data font-bold text-lg mt-0.5 text-mb-emerald">
                          {data.loss_curve.stop_held_count} / {data.losses}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-500">Stops breached</div>
                        <div className={`font-mono-data font-bold text-lg mt-0.5 ${data.loss_curve.stop_breaches > 0 ? "text-red-700" : "text-neutral-400"}`}>
                          {data.loss_curve.stop_breaches}
                        </div>
                      </div>
                    </div>
                    {data.loss_curve.stop_breaches === 0 && (
                      <p className="mt-3 text-xs text-mb-emerald">
                        ✓ Every loss closed at or above the published stop — no gap-down disasters in this window.
                      </p>
                    )}
                  </>
                )}

                {/* Hold-window stats — the "how long do I have to babysit this?" answer */}
                {data.hold_stats && data.hold_stats.avg_days_held > 0 && (
                  <div className={data.losses > 0 ? "mt-5 pt-5 border-t border-mb-border" : ""}>
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                      <div className="label-uppercase text-neutral-500">Hold window</div>
                      <div className="text-xs text-neutral-500">How long do you have to babysit a position?</div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-3 text-sm" data-testid="tr-hold-stats">
                      <div>
                        <div className="text-xs text-neutral-500">Average hold</div>
                        <div className="font-mono-data font-bold text-lg mt-0.5 text-mb-ink">
                          {data.hold_stats.avg_days_held} <span className="text-sm text-neutral-500">days</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-500">Fastest win</div>
                        <div className="font-mono-data font-bold text-lg mt-0.5 text-mb-emerald">
                          {data.hold_stats.fastest_win_days != null ? data.hold_stats.fastest_win_days : "—"}
                          {data.hold_stats.fastest_win_days != null && <span className="text-sm text-neutral-500"> day{data.hold_stats.fastest_win_days === 1 ? "" : "s"}</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-500">Longest hold</div>
                        <div className="font-mono-data font-bold text-lg mt-0.5 text-mb-ink">
                          {data.hold_stats.longest_hold_days != null ? data.hold_stats.longest_hold_days : "—"}
                          {data.hold_stats.longest_hold_days != null && <span className="text-sm text-neutral-500"> days</span>}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-neutral-500">
                      Max hold by design:{" "}
                      {data.hold_stats.has_steady && <>☕ Steady <span className="font-mono-data">{data.hold_stats.steady_max}d</span></>}
                      {data.hold_stats.has_steady && data.hold_stats.has_moonshot && " · "}
                      {data.hold_stats.has_moonshot && <>🚀 Moonshot <span className="font-mono-data">{data.hold_stats.moonshot_max}d</span></>}
                      {" "}— trades that hit TP1 close earlier; trades that hit stop close immediately.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Desk lift — counterfactual ("our judgment adds X pp") */}
            {data.desk_lift && data.desk_lift.total_candidates > data.desk_lift.shipped && (
              <div className="mt-4 bg-mb-emerald text-white rounded-lg p-5" data-testid="tr-desk-lift">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="label-uppercase text-white/70">The desk&apos;s edge</div>
                    <div className="font-display text-2xl font-medium mt-1">
                      Our analyst picks beat the raw algorithm by{" "}
                      <span className="italic">+{data.desk_lift.lift_pp} percentage points.</span>
                    </div>
                    <p className="text-sm text-white/80 mt-2 max-w-2xl">
                      The screener flagged {data.desk_lift.total_candidates} candidates in this window.
                      The desk shipped {data.desk_lift.shipped} of them — {data.desk_lift.approved_wr}% won.
                      Had we published every candidate the algo found, win rate would have been just {data.desk_lift.unfiltered_wr}%.
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-data text-4xl font-bold">+{data.desk_lift.lift_pp}<span className="text-2xl">pp</span></div>
                    <div className="text-xs text-white/70 mt-1">Desk vetting lift</div>
                  </div>
                </div>

                {/* The story: one striking before/after pair */}
                {data.desk_lift.contrast && (
                  <div className="mt-5 pt-5 border-t border-white/15 grid md:grid-cols-2 gap-4" data-testid="tr-desk-contrast">
                    <div className="bg-white/10 rounded-md p-4">
                      <div className="label-uppercase text-emerald-200">✓ Shipped · {new Date(data.desk_lift.contrast.shipped.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                      <div className="mt-2 flex items-baseline justify-between">
                        <span className="font-mono-data text-2xl font-bold tracking-wide" data-testid="contrast-shipped-symbol">{data.desk_lift.contrast.shipped.symbol}</span>
                        <span className="font-mono-data text-2xl font-bold text-emerald-300">
                          +{data.desk_lift.contrast.shipped.return_pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs text-white/70 mt-1">
                        {data.desk_lift.contrast.shipped.outcome === "WIN_MOON" ? "🌙 Moon target hit" :
                         data.desk_lift.contrast.shipped.outcome === "WIN_TP" ? "TP1 hit" : "Win on hold period"}
                        {" · "}{data.desk_lift.contrast.shipped.alert_class === "moonshot" ? "Moonshot" : "Steady"} tier
                      </div>
                    </div>
                    <div className="bg-black/20 rounded-md p-4">
                      <div className="label-uppercase text-red-200">✗ Passed on · {new Date(data.desk_lift.contrast.skipped.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                      <div className="mt-2 flex items-baseline justify-between">
                        <span className="font-mono-data text-2xl font-bold tracking-wide" data-testid="contrast-skipped-symbol">{data.desk_lift.contrast.skipped.symbol}</span>
                        <span className="font-mono-data text-2xl font-bold text-red-300">
                          {data.desk_lift.contrast.skipped.return_pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="text-xs text-white/70 mt-1">
                        {data.desk_lift.contrast.skipped.outcome === "LOSS_SL" ? "Stop hit" : "Loss on hold period"}
                        {" · "}{data.desk_lift.contrast.skipped.alert_class === "moonshot" ? "Moonshot" : "Steady"} tier · subscribers never saw it
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="mt-4 text-xs text-neutral-500">
              {data.total_alerts} published alerts {data.window_days < 9999 ? `in the last ${data.window_days} days` : "since inception"} · {data.closed} closed · {data.active} still in-flight · cross-class duplicates de-duped
            </p>

            {/* How the scoring model works — anchored for the landing link,
                collapsed by default so the win-rate headline keeps the top. */}
            <details
              id="how-signals-work"
              className="mt-8 bg-white border border-mb-border rounded-lg group scroll-mt-24"
              data-testid="how-signals-work"
            >
              <summary className="cursor-pointer list-none flex items-center justify-between p-5">
                <span className="font-display font-semibold text-base sm:text-lg text-mb-ink">
                  How the scoring model works
                </span>
                <HelpCircle className="w-4 h-4 text-mb-emerald flex-shrink-0 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-5 pb-5 -mt-1 text-sm text-neutral-700 leading-relaxed space-y-4">
                <p>
                  One scoring model. Ten signals. Weighted and scored on every tick. When enough signals agree, an alert publishes.
                  The <span className="font-semibold text-mb-ink">number of confirmed signals</span> is what sorts the alert into
                  Steady (confirmed, tight setups) or Moonshot (full stack aligned, rarer setups with bigger upside).
                </p>

                <ul className="space-y-2.5 pt-2 border-t border-mb-border">
                  {[
                    ["Institutional Flow", "sees when large accounts start accumulating"],
                    ["Pressure Release",   "pinpoints the moment a stock breaks its holding pattern"],
                    ["Trend Stack",        "filters for stocks with structural strength beneath them"],
                    ["Momentum Posture",   "screens out the exhausted moves, keeps the fresh ones"],
                    ["Ignition",           "waits for the confluence that historically precedes the run"],
                    ["Entry Zone",         "flags the exact price range where risk/reward is asymmetric"],
                    ["Coiled Spring",      "our proprietary compression signal detects the calm before the storm"],
                    ["Stealth Loading",    "we track the footprints big buyers leave before the crowd notices"],
                    ["Relative Lift",      "spots stocks quietly outperforming while everyone's watching something else"],
                    ["Conviction Stack",   "pulls insider filings, short-interest pressure, and catalyst calendars into one score"],
                  ].map(([name, desc]) => (
                    <li key={name} className="flex items-start gap-2 pt-2.5">
                      <span className="w-1 h-1 rounded-full bg-mb-emerald mt-2 flex-shrink-0" />
                      <span>
                        <span className="font-semibold text-mb-ink">{name}</span>
                        <span className="text-neutral-500"> — {desc}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="pt-4 border-t border-mb-border grid sm:grid-cols-2 gap-3">
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                    <div className="label-uppercase text-[10px] text-emerald-700">Steady</div>
                    <div className="text-xs text-neutral-700 mt-1">
                      Confirmed signals aligned. <span className="font-mono-data">TP1 +2.5% · Moon +12% · Stop −7%</span>
                    </div>
                  </div>
                  <div className="rounded-md bg-pink-50 border border-pink-200 p-3">
                    <div className="label-uppercase text-[10px] text-pink-700">Moonshot</div>
                    <div className="text-xs text-neutral-700 mt-1">
                      Full stack aligned. <span className="font-mono-data">TP1 +6% · Moon +20% · Stop −10%</span>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            {/* Trades table */}
            <div className="mt-8 bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="tr-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-mb-border">
                    <tr>
                      <Th>Symbol</Th><Th>Score</Th><Th>Class</Th><Th>Tier</Th><Th>Alert</Th><Th>Entry</Th>
                      <Th>TP1</Th><Th>Moon</Th><Th>Stop</Th><Th>Days</Th><Th>Outcome</Th><Th>Return</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.alerts.length === 0 && (
                      <tr><td colSpan="12" className="px-4 py-12 text-center text-neutral-500">No alerts in this window yet.</td></tr>
                    )}
                    {data.alerts.map((a) => {
                      const o = a.outcome || {};
                      const sb = STATUS_BADGE[o.status] || STATUS_BADGE.UNKNOWN;
                      const ret = o.return_pct || 0;
                      const isWin = (o.status || "").startsWith("WIN");
                      const isLoss = (o.status || "").startsWith("LOSS");
                      return (
                        <tr key={a.id} className="border-b border-neutral-100" data-testid={`tr-row-${a.symbol}`}>
                          <Td className="font-mono-data font-bold">{a.symbol}</Td>
                          <Td><ScorePill grade={a.moonbucks_grade} size="xs" /></Td>
                          <Td><AlertClassBadge alertClass={a.alert_class || "steady"} compact /></Td>
                          <Td className="font-mono-data text-xs label-uppercase text-neutral-500">{a.tier}</Td>
                          <Td className="font-mono-data text-xs text-neutral-500">{a.created_at?.slice(0,10)}</Td>
                          <Td className="font-mono-data">${a.entry_price}</Td>
                          <Td className="font-mono-data text-emerald-700">${a.take_profit_price}</Td>
                          <Td className="font-mono-data text-amber-600">${a.moon_target_price ?? "—"}</Td>
                          <Td className="font-mono-data text-red-700">${a.stop_loss_price}</Td>
                          <Td className="font-mono-data">{o.days_elapsed ?? 0}d</Td>
                          <Td><span className={`${sb.bg} px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider whitespace-nowrap`}>{sb.label}</span></Td>
                          <Td className={`font-mono-data font-bold ${isWin ? "text-buy" : isLoss ? "text-sell" : "text-neutral-500"}`}>
                            {ret >= 0 ? "+" : ""}{ret}%
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-12 bg-mb-emerald text-white rounded-lg p-8 text-center">
              <Rocket className="w-10 h-10 mx-auto mb-3" />
              <h2 className="font-display font-semibold text-3xl tracking-tight">Stop watching from the sidelines.</h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">For less than a cup of coffee, get every published alert delivered the moment it goes live.</p>
              <Link to="/register">
                <Button className="mt-6 rounded-md bg-white text-mb-emerald hover:bg-neutral-100 font-bold px-8" data-testid="tr-bottom-cta">
                  Activate Moonbucks
                </Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const Big = ({ label, val, highlight }) => (
  <div className={`bg-white border ${highlight ? "border-mb-emerald" : "border-mb-border"} rounded-lg p-5`}>
    <div className="label-uppercase text-neutral-500">{label}</div>
    <div className={`mt-2 font-mono-data text-3xl font-extrabold ${highlight ? "text-mb-emerald" : ""}`}>{val}</div>
  </div>
);

function ClassBucket({ kind, title, stats }) {
  const isMoon = kind === "moonshot";
  if (!stats || stats.total === 0) {
    return (
      <div className={`rounded-lg border-2 p-5 ${isMoon ? "border-pink-200 bg-pink-50/30" : "border-mb-emerald/20 bg-mb-emerald/5"}`}>
        <div className="font-display font-bold text-base">{title}</div>
        <div className="text-sm text-neutral-500 mt-2">No alerts in window yet.</div>
      </div>
    );
  }
  return (
    <div
      className={`rounded-lg p-5 ${isMoon ? "bg-gradient-to-br from-pink-500 to-pink-700 text-white" : "bg-mb-emerald text-white"}`}
      data-testid={`tr-bucket-${kind}`}
    >
      <div className="font-display font-semibold text-lg">{title}</div>
      <div className="text-[10px] text-white/70 font-mono-data tracking-wide mt-0.5">LIVE · POST-DESK-VETTING</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <BucketStat label="Win Rate" val={`${stats.win_rate}%`} />
        <BucketStat label="Avg Return" val={`${stats.avg_return_pct >= 0 ? "+" : ""}${stats.avg_return_pct}%`} />
        <BucketStat label="🌙 Moons" val={stats.moon_hits} />
        <BucketStat label="Closed" val={`${stats.wins}W · ${stats.closed - stats.wins}L`} />
      </div>
      <div className="text-[10px] text-white/70 mt-2 font-mono-data">
        {stats.total} total · best trade {stats.best_pct >= 0 ? "+" : ""}{stats.best_pct}%{" "}
        <span className="text-white/50">· algo-only: {isMoon ? "59.3%" : "87.5%"} WR</span>
      </div>
      {!isMoon && (
        <div className="text-[10px] text-white/55 mt-1 leading-snug">
          Small live sample — algo&apos;s 87.5% is the more statistically robust number here.
        </div>
      )}
    </div>
  );
}

const BucketStat = ({ label, val }) => (
  <div className="bg-white/10 rounded-lg p-2">
    <div className="label-uppercase text-white/60 text-[9px]">{label}</div>
    <div className="font-mono-data font-extrabold mt-0.5">{val}</div>
  </div>
);
const Th = ({ children }) => <th className="px-4 py-3 text-left label-uppercase text-neutral-500 whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
